// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file renderDropdownItems.tsx
 * @output Converts data-driven menu items into DropdownMenuItem components
 * @position Utility; used by DropdownMenu to unify data-driven and compound paths
 */

import * as stylex from '@stylexjs/stylex';
import { Divider } from "../Divider/index.js";
import { DropdownMenuItem } from "./DropdownMenuItem.js";
import { DropdownMenuSubMenu } from "./DropdownMenuSubMenu.js";
import "../theme/tokens.stylex.js";
import { spacingVars, typographyVars, typeScaleVars, colorVars } from "../theme/tokens.stylex.js";
import { mergeProps, themeProps } from "../utils/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  divider: {
    kqGvvJ: "xsq74q5",
    $$css: true
  }
};
function getItemKey(item) {
  return `item-${item.label}`;
}
function getSectionKey(section, index) {
  return `section-${section.title ?? index}`;
}

/**
 * Renders one leaf row as a `DropdownMenuItem`.
 *
 * `items` is stripped because it selects the submenu shape rather than being an
 * item prop; every other field of `DropdownMenuItemData` is a
 * `DropdownMenuItem` prop by construction (the type is `Pick`ed from
 * `DropdownMenuItemProps`), so the data path forwards them wholesale and can't
 * silently drop a field the data API advertises.
 */
function renderLeafItem(item) {
  const {
    items: _submenuItems,
    ...itemProps
  } = item;
  return /*#__PURE__*/_jsx(DropdownMenuItem, {
    ...itemProps
  }, getItemKey(item));
}

/**
 * Converts data-driven items into DropdownMenuItem components,
 * so both modes share the same rendering and keyboard navigation path.
 */
export function renderDropdownItems(items) {
  const elements = [];
  for (let i = 0; i < items.length; i++) {
    const option = items[i];
    if ('type' in option && option.type === 'divider') {
      elements.push(/*#__PURE__*/_jsx(Divider, {
        xstyle: styles.divider,
        ...themeProps('dropdown-menu-divider')
      }, `divider-${i}`));
    } else if ('type' in option && option.type === 'section') {
      elements.push(/*#__PURE__*/_jsxs("div", {
        role: "group",
        "aria-label": option.title,
        children: [option.title && /*#__PURE__*/_jsx("div", {
          "aria-hidden": "true",
          ...mergeProps(themeProps('dropdown-menu-section-heading'), {
            className: "xu0wf1k xf314gf x9ynric x141an7d x1ltkj2j xv1l7n4 x87ps6o"
          }),
          children: option.title
        }), option.items.map(renderLeafItem)]
      }, getSectionKey(option, i)));
    } else if (!('type' in option)) {
      // A plain item that declares nested `items` becomes a submenu (data-mode
      // parity with the compound DropdownMenuSubMenu API); otherwise it's a
      // leaf. renderDropdownItems owns the recursion and hands the rendered
      // children to DropdownMenuSubMenu — so DropdownMenuSubMenu never imports
      // this module back (no import cycle).
      if (option.items && option.items.length > 0) {
        elements.push(/*#__PURE__*/_jsx(DropdownMenuSubMenu, {
          icon: option.icon,
          label: option.label,
          isDisabled: option.isDisabled,
          children: renderDropdownItems(option.items)
        }, getItemKey(option)));
      } else {
        elements.push(renderLeafItem(option));
      }
    }
  }
  return elements;
}